import React, { useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import { Button, TextField, TitleSubtitle } from "../..";
import { CreatePasswordContainer, PasswordRule, PasswordRuleContainer, PasswordRuleIcon, PasswordRuleText, EyeIconContainer } from "./Password.styles";
import { CTAForthContainer } from "../../../pages/Register/Register.styles";
import { useRegisterContext } from "../../../contexts/RegisterContext";
import { Icon } from "../../../icons";

interface PasswordProps extends WithRouterProps {
    onNextButtonClick: Function;
}

const Password: React.FC<PasswordProps> = ({
    onNextButtonClick
}) => {
    const atLeastOneLetter = /^.*[A-Za-z].*$/;
    const atLeastOneNumber = /^.*\d.*$/;
    const atLeastOneSpecialCharacter = /^.*[!@#$%^&*=?].*$/;
    const between8and16CharactersLong = /^.{8,16}$/;

    const { registerInfo, setRegisterInfo } = useRegisterContext();

    const [isAtLeastOneLetter, setIsAtLeastOneLetter] = useState<boolean>(atLeastOneLetter.test(registerInfo.user.password));
    const [isAtLeastOneNumber, setIsAtLeastOneNumber] = useState<boolean>(atLeastOneNumber.test(registerInfo.user.password));
    const [isAtLeastOneSpecialCharacter, setIsAtLeastOneSpecialCharacter] = useState<boolean>(atLeastOneSpecialCharacter.test(registerInfo.user.password));
    const [isBetween8and16CharactersLong, setIsBetween8and16CharactersLong] = useState<boolean>(between8and16CharactersLong.test(registerInfo.user.password));

    const [showPassword, setShowPassword] = useState(false);

    const isContinueButtonDisabled = () => {
        return !(isAtLeastOneLetter && isAtLeastOneNumber && isAtLeastOneSpecialCharacter && isBetween8and16CharactersLong);
    }

    const validatePassword = (passwd: string) => {
        setIsAtLeastOneLetter(atLeastOneLetter.test(passwd));
        setIsAtLeastOneNumber(atLeastOneNumber.test(passwd));
        setIsAtLeastOneSpecialCharacter(atLeastOneSpecialCharacter.test(passwd));
        setIsBetween8and16CharactersLong(between8and16CharactersLong.test(passwd));
    }

    const onPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRegisterInfo(prevRegisterInfo => ({
            ...prevRegisterInfo,
            user: {
                ...prevRegisterInfo.user,
                password: event.target.value
            }
        }));
        validatePassword(event.target.value);
    }

    const ShowHidePassword = () => {
        return (
            <EyeIconContainer onClick={() => setShowPassword(!showPassword)}>
                <Icon icon={showPassword ? "EyeOff" : "Eye" } size="sm" />
            </EyeIconContainer>
        )
    }

    return (
        <CreatePasswordContainer>
            <TitleSubtitle
                data-cy="register-create-password-title"
                size="sm"
                title="Create a Password"
                subTitle="You’ll need this to manage your bookings online" />
            <TextField
                data-cy="register-password"
                autoFocus
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={registerInfo.user.password}
                onChange={onPasswordChange} 
                rightIcon={<ShowHidePassword />}
            />
            <PasswordRuleContainer>
                <PasswordRule className={isAtLeastOneLetter ? "is-checked" : ""}>
                    <PasswordRuleIcon
                        data-cy="register-password-one-letter"
                        icon={isAtLeastOneLetter ? "Check" : "Plus"} />
                    <PasswordRuleText> At least one letter </PasswordRuleText>
                </PasswordRule>
                <PasswordRule className={isAtLeastOneNumber ? "is-checked" : ""}>
                    <PasswordRuleIcon
                        data-cy="register-password-one-number"
                        icon={isAtLeastOneNumber ? "Check" : "Plus"} />
                    <PasswordRuleText> At least one number </PasswordRuleText>
                </PasswordRule>
                <PasswordRule className={isAtLeastOneSpecialCharacter ? "is-checked" : ""}>
                    <PasswordRuleIcon
                        data-cy="register-password-one-special-character"
                        icon={isAtLeastOneSpecialCharacter ? "Check" : "Plus"} />
                    <PasswordRuleText> At least one special character (!@#$%^&*=?) </PasswordRuleText>
                </PasswordRule>
                <PasswordRule className={isBetween8and16CharactersLong ? "is-checked" : ""}>
                    <PasswordRuleIcon
                        data-cy="register-password-length"
                        icon={isBetween8and16CharactersLong ? "Check" : "Plus"} />
                    <PasswordRuleText> Be between 8 - 16 characters long </PasswordRuleText>
                </PasswordRule>
            </PasswordRuleContainer>
            <CTAForthContainer>
                <Button
                    data-cy="register-password-submit"
                    onClick={() => onNextButtonClick()}
                    disabled={isContinueButtonDisabled()}
                > Continue </Button>
            </CTAForthContainer>
        </CreatePasswordContainer>
    );
};

Password.displayName = "Password";

const ComponentWithRouter = withRouter(Password)

export { ComponentWithRouter as Password };
